import React, { useEffect, useMemo, useState } from 'react';

import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';

import {
  Alert,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  Row,
  Spinner,
} from 'reactstrap';
import toastr from 'toastr';
import 'toastr/build/toastr.min.css';

document.title = 'UCP Utilities';

function UcpUtilities(props) {
  const { t } = props;

  const defaultBaseUrl = useMemo(() => `${window.location.protocol}//${window.location.hostname}/ucp`, []);

  const [baseUrl, setBaseUrl] = useState(defaultBaseUrl);
  const [discoveryOutput, setDiscoveryOutput] = useState('');
  const [searchBody, setSearchBody] = useState(JSON.stringify({
    query: 'W10642928',
    pagination: {
      limit: 5,
    },
  }, null, 2));
  const [searchOutput, setSearchOutput] = useState('');
  const [lookupBody, setLookupBody] = useState(JSON.stringify({
    ids: ['listing:340683'],
  }, null, 2));
  const [lookupOutput, setLookupOutput] = useState('');
  const [checkoutBody, setCheckoutBody] = useState(JSON.stringify({
    currency: 'USD',
    line_items: [
      {
        item: {
          id: 'listing:340683',
          title: 'Example Listing',
          price: 49.99,
        },
        quantity: 1,
      },
    ],
  }, null, 2));
  const [checkoutOutput, setCheckoutOutput] = useState('');
  const [completeCheckoutId, setCompleteCheckoutId] = useState('chk_replace_me');
  const [completeBody, setCompleteBody] = useState(JSON.stringify({
    payment: {
      instruments: [
        {
          id: 'gpay_test_instrument',
          handler_id: 'google-pay-authorizenet',
          name: 'com.google.pay',
          opaqueData: {
            dataDescriptor: 'COMMON.GOOGLE.INAPP.PAYMENT',
            dataValue: 'PASTE_GOOGLE_PAY_TOKEN_HERE',
          },
        },
      ],
    },
  }, null, 2));
  const [completeOutput, setCompleteOutput] = useState('');
  const [authorizeDebugOutput, setAuthorizeDebugOutput] = useState('');
  const [googlePayConfig, setGooglePayConfig] = useState(null);
  const [googlePayReady, setGooglePayReady] = useState(false);
  const [googlePayToken, setGooglePayToken] = useState('');
  const [googlePayOutput, setGooglePayOutput] = useState('');
  const [loadingKey, setLoadingKey] = useState(null);

  toastr.options = {
    positionClass: 'toast-top-right',
    newestOnTop: true,
  };

  useEffect(() => {
    if (window.google?.payments?.api) {
      return undefined;
    }

    const script = document.createElement('script');
    script.src = 'https://pay.google.com/gp/p/js/pay.js';
    script.async = true;
    script.onload = () => setGooglePayReady(true);
    script.onerror = () => toastr.error(t('Failed to load Google Pay JavaScript'), t('Error'));
    document.body.appendChild(script);

    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, [t]);

  useEffect(() => {
    if (window.google?.payments?.api) {
      setGooglePayReady(true);
    }
  }, []);

  const parseResponseText = (text) => {
    let parsedBody = text;

    try {
      parsedBody = JSON.parse(text);
    } catch (error) {
      // keep text response as-is
    }

    return parsedBody;
  };

  const request = async ({
    key, path, method = 'GET', body = null, setOutput, onSuccess, onResponse,
  }) => {
    try {
      setLoadingKey(key);
      const response = await fetch(`${baseUrl}${path}`, {
        method,
        headers: {
          Accept: 'application/json',
          ...(body ? { 'Content-Type': 'application/json' } : {}),
        },
        body,
      });

      const text = await response.text();
      const parsedBody = parseResponseText(text);
      const output = JSON.stringify({
        status: response.status,
        statusText: response.statusText,
        headers: Object.fromEntries(response.headers.entries()),
        body: parsedBody,
      }, null, 2);
      setOutput(output);

      if (onResponse) {
        onResponse(parsedBody, response);
      }

      if (!response.ok) {
        toastr.error(t('Request failed'));
        return;
      }

      if (onSuccess) {
        onSuccess(parsedBody);
      }

      toastr.success(t('Request complete'));
    } catch (error) {
      setOutput(JSON.stringify({
        error: error.message,
      }, null, 2));
      toastr.error(error.message, t('Error'));
    } finally {
      setLoadingKey(null);
    }
  };

  const fetchJson = async ({
    key, path, method = 'GET', body = null,
  }) => {
    setLoadingKey(key);

    try {
      const response = await fetch(`${baseUrl}${path}`, {
        method,
        headers: {
          Accept: 'application/json',
          ...(body ? { 'Content-Type': 'application/json' } : {}),
        },
        body,
      });

      const text = await response.text();
      let parsed = null;

      if (text) {
        try {
          parsed = JSON.parse(text);
        } catch (error) {
          parsed = null;
        }
      }

      if (!response.ok) {
        throw new Error(parsed?.error || text || `${response.status} ${response.statusText}`);
      }

      return parsed;
    } finally {
      setLoadingKey(null);
    }
  };

  const submitJsonRequest = async ({
    key, path, method, rawBody, setOutput, onSuccess, onResponse,
  }) => {
    let parsed;

    try {
      parsed = JSON.parse(rawBody);
    } catch (error) {
      toastr.error(t('Request body must be valid JSON'), t('Error'));
      setOutput(JSON.stringify({ error: error.message }, null, 2));
      return;
    }

    await request({
      key,
      path,
      method,
      body: JSON.stringify(parsed),
      setOutput,
      onSuccess,
      onResponse,
    });
  };

  const renderOutput = (title, output) => (
    <div className="mt-3">
      <Label className="fw-bold">{title}</Label>
      <pre
        className="ucp-utilities__output bg-light border rounded p-3 mb-0"
        style={{
          minHeight: '180px',
          maxHeight: '420px',
          overflow: 'auto',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
        }}
      >
        {output || t('No output yet')}
      </pre>
    </div>
  );

  const extractAuthorizeDebugOutput = (body) => {
    const debugPayload = body?.payment?.authorization?.debug_request
      || body?.authorize_request
      || body?.processor_response?.payment?.authorization?.debug_request
      || null;

    return debugPayload ? JSON.stringify(debugPayload, null, 2) : '';
  };

  const launchGooglePay = async () => {
    if (!window.google?.payments?.api) {
      toastr.error(t('Google Pay JavaScript is not loaded yet'), t('Error'));
      return;
    }

    try {
      const payload = await fetchJson({
        key: 'google-pay-config',
        path: '/payments/google-pay/config',
      });
      const config = payload?.google_pay;

      if (!config) {
        throw new Error('Google Pay config was not returned by the server.');
      }

      setGooglePayConfig(config);

      const paymentsClient = new window.google.payments.api.PaymentsClient({
        environment: config.environment || 'TEST',
      });
      const transactionPrice = (() => {
        try {
          const parsed = JSON.parse(checkoutBody);
          const total = (parsed.line_items || []).reduce((sum, lineItem) => {
            const price = Number(lineItem?.item?.price || 0);
            const quantity = Number(lineItem?.quantity || 1);
            return sum + (price * quantity);
          }, 0);

          return total > 0 ? total.toFixed(2) : '1.00';
        } catch (error) {
          return '1.00';
        }
      })();

      const paymentDataRequest = {
        apiVersion: 2,
        apiVersionMinor: 0,
        allowedPaymentMethods: config.allowedPaymentMethods,
        merchantInfo: config.merchantInfo,
        transactionInfo: {
          totalPriceStatus: 'FINAL',
          totalPrice: transactionPrice,
          currencyCode: 'USD',
          countryCode: 'US',
        },
      };

      const paymentData = await paymentsClient.loadPaymentData(paymentDataRequest);
      const token = paymentData?.paymentMethodData?.tokenizationData?.token || '';
      const tokenPayload = {
        paymentData,
        opaqueData: {
          dataDescriptor: 'COMMON.GOOGLE.INAPP.PAYMENT',
          dataValue: token,
        },
      };
      const formatted = JSON.stringify(tokenPayload, null, 2);

      setGooglePayToken(token);
      setGooglePayOutput(formatted);
      setCompleteBody(JSON.stringify({
        payment: {
          instruments: [
            {
              id: 'gpay_wallet_instrument',
              handler_id: 'google-pay-authorizenet',
              name: 'com.google.pay',
              opaqueData: {
                dataDescriptor: 'COMMON.GOOGLE.INAPP.PAYMENT',
                dataValue: token,
              },
              paymentMethodData: paymentData?.paymentMethodData,
            },
          ],
        },
      }, null, 2));

      toastr.success(t('Google Pay token captured'));
    } catch (error) {
      setGooglePayOutput(JSON.stringify({
        error: error.message,
      }, null, 2));
      toastr.error(error.message, t('Error'));
    }
  };

  const copyGooglePayPayload = async () => {
    try {
      const value = googlePayToken || googlePayOutput;

      if (!value) {
        return;
      }

      await navigator.clipboard.writeText(value);
      toastr.success(t('Copied to clipboard'));
    } catch (error) {
      toastr.error(error.message, t('Error'));
    }
  };

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Row>
          <Col xl="10">
            <Card>
              <CardHeader className="bg-transparent border-bottom">
                {t('UCP Utilities')}
              </CardHeader>
              <CardBody>
                <Alert color="info" className="mb-4">
                  {t('Use this page to explore the standalone UCP REST service from the admin UI.')}
                </Alert>

                <FormGroup>
                  <Label htmlFor="ucp-base-url">{t('UCP Base URL')}</Label>
                  <Input
                    id="ucp-base-url"
                    value={baseUrl}
                    onChange={(event) => setBaseUrl(event.target.value)}
                    placeholder="http://localhost:3026"
                  />
                </FormGroup>

                <Row className="g-4">
                  <Col lg="6">
                    <Card className="h-100">
                      <CardHeader className="bg-transparent border-bottom">{t('Discovery')}</CardHeader>
                      <CardBody>
                        <p className="text-muted mb-3">{t('Fetch the UCP business profile document.')}</p>
                        <Button
                          color="primary"
                          onClick={() => request({
                            key: 'discovery',
                            path: '/.well-known/ucp',
                            setOutput: setDiscoveryOutput,
                          })}
                          disabled={loadingKey !== null}
                        >
                          {loadingKey === 'discovery' && <Spinner size="sm" className="me-2" />}
                          {t('GET /.well-known/ucp')}
                        </Button>
                        {renderOutput(t('Discovery Output'), discoveryOutput)}
                      </CardBody>
                    </Card>
                  </Col>

                  <Col lg="6">
                    <Card className="h-100">
                      <CardHeader className="bg-transparent border-bottom">{t('Catalog Search')}</CardHeader>
                      <CardBody>
                        <Form onSubmit={(event) => {
                          event.preventDefault();
                          submitJsonRequest({
                            key: 'search',
                            path: '/catalog/search',
                            method: 'POST',
                            rawBody: searchBody,
                            setOutput: setSearchOutput,
                          });
                        }}
                        >
                          <FormGroup>
                            <Label htmlFor="ucp-search-body">{t('Request JSON')}</Label>
                            <Input
                              id="ucp-search-body"
                              type="textarea"
                              value={searchBody}
                              onChange={(event) => setSearchBody(event.target.value)}
                              style={{ minHeight: '180px', fontFamily: 'monospace' }}
                            />
                          </FormGroup>
                          <Button color="primary" type="submit" disabled={loadingKey !== null}>
                            {loadingKey === 'search' && <Spinner size="sm" className="me-2" />}
                            {t('POST /catalog/search')}
                          </Button>
                        </Form>
                        {renderOutput(t('Search Output'), searchOutput)}
                      </CardBody>
                    </Card>
                  </Col>

                  <Col lg="6">
                    <Card className="h-100">
                      <CardHeader className="bg-transparent border-bottom">{t('Catalog Lookup')}</CardHeader>
                      <CardBody>
                        <Form onSubmit={(event) => {
                          event.preventDefault();
                          submitJsonRequest({
                            key: 'lookup',
                            path: '/catalog/lookup',
                            method: 'POST',
                            rawBody: lookupBody,
                            setOutput: setLookupOutput,
                          });
                        }}
                        >
                          <FormGroup>
                            <Label htmlFor="ucp-lookup-body">{t('Request JSON')}</Label>
                            <Input
                              id="ucp-lookup-body"
                              type="textarea"
                              value={lookupBody}
                              onChange={(event) => setLookupBody(event.target.value)}
                              style={{ minHeight: '180px', fontFamily: 'monospace' }}
                            />
                          </FormGroup>
                          <Button color="primary" type="submit" disabled={loadingKey !== null}>
                            {loadingKey === 'lookup' && <Spinner size="sm" className="me-2" />}
                            {t('POST /catalog/lookup')}
                          </Button>
                        </Form>
                        {renderOutput(t('Lookup Output'), lookupOutput)}
                      </CardBody>
                    </Card>
                  </Col>

                  <Col lg="6">
                    <Card className="h-100">
                      <CardHeader className="bg-transparent border-bottom">{t('Checkout Session')}</CardHeader>
                      <CardBody>
                        <Form onSubmit={(event) => {
                          event.preventDefault();
                          submitJsonRequest({
                            key: 'checkout',
                            path: '/checkout-sessions',
                            method: 'POST',
                            rawBody: checkoutBody,
                            setOutput: setCheckoutOutput,
                            onSuccess: (body) => {
                              if (body?.id) {
                                setCompleteCheckoutId(body.id);
                              }
                            },
                          });
                        }}
                        >
                          <FormGroup>
                            <Label htmlFor="ucp-checkout-body">{t('Request JSON')}</Label>
                            <Input
                              id="ucp-checkout-body"
                              type="textarea"
                              value={checkoutBody}
                              onChange={(event) => setCheckoutBody(event.target.value)}
                              style={{ minHeight: '180px', fontFamily: 'monospace' }}
                            />
                          </FormGroup>
                          <Button color="primary" type="submit" disabled={loadingKey !== null}>
                            {loadingKey === 'checkout' && <Spinner size="sm" className="me-2" />}
                            {t('POST /checkout-sessions')}
                          </Button>
                        </Form>
                        {renderOutput(t('Checkout Output'), checkoutOutput)}
                      </CardBody>
                    </Card>
                  </Col>

                  <Col lg="6">
                    <Card className="h-100">
                      <CardHeader className="bg-transparent border-bottom">{t('Complete Checkout')}</CardHeader>
                      <CardBody>
                        <p className="text-muted mb-3">
                          {t('Submit a Google Pay-style payment instrument to complete a checkout session through Authorize.net.')}
                        </p>
                        <Form onSubmit={(event) => {
                          event.preventDefault();
                          submitJsonRequest({
                            key: 'complete',
                            path: `/checkout-sessions/${completeCheckoutId}/complete`,
                            method: 'POST',
                            rawBody: completeBody,
                            setOutput: setCompleteOutput,
                            onResponse: (body) => {
                              setAuthorizeDebugOutput(extractAuthorizeDebugOutput(body));
                            },
                          });
                        }}
                        >
                          <FormGroup>
                            <Label htmlFor="ucp-complete-checkout-id">{t('Checkout ID')}</Label>
                            <Input
                              id="ucp-complete-checkout-id"
                              value={completeCheckoutId}
                              onChange={(event) => setCompleteCheckoutId(event.target.value)}
                              placeholder="chk_replace_me"
                            />
                          </FormGroup>
                          <FormGroup>
                            <Label htmlFor="ucp-complete-body">{t('Request JSON')}</Label>
                            <Input
                              id="ucp-complete-body"
                              type="textarea"
                              value={completeBody}
                              onChange={(event) => setCompleteBody(event.target.value)}
                              style={{ minHeight: '180px', fontFamily: 'monospace' }}
                            />
                          </FormGroup>
                          <Button color="primary" type="submit" disabled={loadingKey !== null}>
                            {loadingKey === 'complete' && <Spinner size="sm" className="me-2" />}
                            {t('POST /checkout-sessions/:id/complete')}
                          </Button>
                        </Form>
                        {renderOutput(t('Complete Output'), completeOutput)}
                        {renderOutput(t('Authorize Request Debug'), authorizeDebugOutput)}
                      </CardBody>
                    </Card>
                  </Col>

                  <Col lg="6">
                    <Card className="h-100">
                      <CardHeader className="bg-transparent border-bottom">{t('Google Pay Token')}</CardHeader>
                      <CardBody>
                        <p className="text-muted mb-3">
                          {t('Launch Google Pay in the browser and store the returned token for checkout completion testing.')}
                        </p>
                        <div className="d-flex gap-2 flex-wrap">
                          <Button
                            color="dark"
                            onClick={launchGooglePay}
                            disabled={loadingKey !== null || !googlePayReady}
                          >
                            {loadingKey === 'google-pay-config' && <Spinner size="sm" className="me-2" />}
                            {t('Launch Google Pay')}
                          </Button>
                          <Button
                            color="secondary"
                            onClick={copyGooglePayPayload}
                            disabled={!googlePayToken && !googlePayOutput}
                          >
                            {t('Copy Token Payload')}
                          </Button>
                        </div>
                        {!googlePayReady && (
                          <Alert color="warning" className="mt-3 mb-0">
                            {t('Google Pay JavaScript is still loading.')}
                          </Alert>
                        )}
                        {googlePayConfig && (
                          <Alert color="success" className="mt-3 mb-0">
                            {t('Google Pay config loaded from the UCP backend.')}
                          </Alert>
                        )}
                        {renderOutput(t('Google Pay Output'), googlePayOutput)}
                      </CardBody>
                    </Card>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
}

UcpUtilities.defaultProps = {
  t: (x) => x,
};

UcpUtilities.propTypes = {
  t: PropTypes.any,
};

export default connect(() => ({}), {})(withTranslation()(UcpUtilities));
